import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'stores/Store';
import { identifyUser, initAnalytics, resetUser, syncAnalyticsConsent } from 'lib/analytics';
import { onConsentChange } from 'lib/consent';

/**
 * Boots PostHog once and identifies the current user whenever authUser
 * transitions from anonymous -> authed (and resets on logout). Kept as a
 * component (not a context) because nothing downstream needs to read from
 * it; the analytics layer is module-scoped.
 *
 * PostHog initializes opted out and stays that way until OneTrust reports
 * C0002 consent. OneTrust is injected asynchronously by Tealium and routinely
 * lands after this mounts, so consent is re-synced on every CMP callback
 * rather than read once at startup.
 */
const PostHogProvider = observer(({ children }) => {
    const { authUser } = useStore();

    useEffect(() => {
        initAnalytics();
        syncAnalyticsConsent();
        return onConsentChange(syncAnalyticsConsent);
    }, []);

    useEffect(() => {
        if (authUser?.Id) {
            identifyUser(authUser.Id, {
                email: authUser.Email || undefined,
                username: authUser.Username || undefined,
                is_vendor: !!authUser.Roles?.Vendor,
                is_admin: !!authUser.Roles?.Admin,
            });
        } else {
            resetUser();
        }
    }, [authUser?.Id, authUser?.Email, authUser?.Username]);

    return children;
});

export { PostHogProvider };
